import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import { actions as mapActions } from 'services/map/map-service';
import { Panel } from 'components';
import './styles.scss';

interface IBalloonProps {
    view: omjs.View,
    coords: [number, number],
    children: React.ReactElement,
    freeSpaceElement?: HTMLElement,
    draggable?: boolean
}

interface IBalloonState {
    showAnchor: boolean
}

export class Balloon extends Component<IBalloonProps, IBalloonState> {
    overlay;
    wrapperElement;
    balloonElement;
    dragHandlerElement;
    offsetX: number = 0;
    offsetY: number = 0;

    constructor(props) {
        super(props);
        this.state = {
            showAnchor: true
        };
        this.wrapperElement = document.createElement('div');
        this.closeBalloon = this.closeBalloon.bind(this);
        this.dragRef = this.dragRef.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onMouseDown = this.onMouseDown.bind(this);
    }

    componentDidUpdate(prevProps) {
        const { children, coords, view } = this.props;
        const { coords: prevCoords, children: prevChildren } = prevProps;
        if (children && !prevChildren) {
            this.addOverlay();
        }
        if (coords && (JSON.stringify(coords) !== JSON.stringify(prevCoords))) {
            this.setPosition();
        }
        if (prevCoords && !coords) {
            view.getMap().removeOverlay(this.overlay);
        }
    }

    setPosition() {
        const { coords, freeSpaceElement } = this.props;
        if (this.balloonElement) {
            this.balloonElement.style = null;
            this.offsetX = 0;
            this.offsetY = 0;
            this.setState({ showAnchor: true });
        }
        this.overlay.setPosition(coords);
        if (freeSpaceElement) {
            setTimeout(() => {
                this.moveBalloon();
            });
        }
    }

    onMove(e) {
        const { showAnchor } = this.state;
        this.offsetX += e.movementX;
        this.offsetY += e.movementY;

        if (showAnchor) {
            this.setState({
                showAnchor: false
            });
        }

        if (this.overlay?.element) {
            this.overlay.element.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px)`;
        }
    }

    onMouseDown() {
        document.addEventListener('mousemove', this.onMove);
        document.addEventListener('mouseup',
            () => document.removeEventListener('mousemove', this.onMove),
            { once: true });
    }

    convertToClick = (e) => {
        const evt = new MouseEvent('click', { bubbles: true });
        evt.stopPropagation = () => {};
        e.target.dispatchEvent(evt);
    };

    moveBalloon() {
        const { view, freeSpaceElement } = this.props;
        const offset = 50;
        view.getMap().renderSync();
        const currentCenter = view.getMap().getView().getCenter();
        let freeSpaceRect = freeSpaceElement.getBoundingClientRect();
        let balloonRect = this.wrapperElement.getBoundingClientRect();
        let currentPixel = [ balloonRect.left, balloonRect.top ];
        let newPixel = [ ...currentPixel ];
        if (freeSpaceRect.left > balloonRect.left) {
            newPixel[0] = freeSpaceRect.left + offset;
        }
        if (freeSpaceRect.top > balloonRect.top) {
            newPixel[1] = freeSpaceRect.top + offset;
        }
        if (freeSpaceRect.right < balloonRect.right) {
            newPixel[0] = (balloonRect.width <= freeSpaceRect.width)
                ? freeSpaceRect.right - balloonRect.width - offset
                : freeSpaceRect.left + offset;
        }
        if (freeSpaceRect.bottom < balloonRect.bottom) {
            newPixel[1] = (balloonRect.height <= freeSpaceRect.height)
                ? freeSpaceRect.bottom - balloonRect.height - offset
                : freeSpaceRect.top + offset;
        }
        let deltaPixelX = currentPixel[0] - newPixel[0];
        let deltaPixelY = currentPixel[1] - newPixel[1];
        if ((deltaPixelX !== 0) || (deltaPixelY !== 0)) {
            let centerPixel = view.getMap().getPixelFromCoordinate(currentCenter);
            let newCenterPixel = [ centerPixel[0] + deltaPixelX, centerPixel[1] + deltaPixelY ];
            let newCenter = view.getMap().getCoordinateFromPixel(newCenterPixel);
            view.getMap().getView().animate({ center: newCenter });
        }
    }

    addOverlay() {
        const { view, coords } = this.props;
        if (this.overlay) {
            view.getMap().removeOverlay(this.overlay);
        }

        this.overlay = new omjs.ol.Overlay({
            element: this.wrapperElement,
            autoPan: false,
            offset: [ 0, -20 ],
            positioning: 'bottom-center'
        });
        view.getMap().addOverlay(this.overlay);
        this.overlay.setPosition(coords);
    }

    closeBalloon() {
        mapActions.hideBalloon();
    }

    dragRef(el) {
        if (!this.dragHandlerElement && el) {
            this.dragHandlerElement = el;
            this.dragHandlerElement.addEventListener('mousedown', this.onMouseDown);
        } else if (this.dragHandlerElement && !el) {
            this.dragHandlerElement.removeEventListener('mousedown', this.onMouseDown);
            this.dragHandlerElement = el;
        }
    }

    renderContent() {
        const { children, draggable } = this.props;
        const { showAnchor } = this.state;

        return (
            <div
                className={clsx('wrapper-balloon', {
                    'with-anchor': showAnchor
                })}
                onMouseUp={this.convertToClick}
                ref={(el) => { this.balloonElement = el; }}
            >
                {draggable && <div className="drag-handler" ref={this.dragRef} />}
                <Panel className="balloon">
                    {children}
                </Panel>
            </div>
        );
    }

    render() {
        if (!this.props.children) return null;
        return ReactDOM.createPortal(
            this.renderContent(),
            this.wrapperElement
        );
    }
}
